import ColorPalette from '@atoms/ColorPalette/ColorPalette';
import Seo from '@atoms/Seo/Seo';
import AccessibilityPanel from '@molecules/Accessiblity/AccessibilityPanel';
import { GoogleTagManager } from '@next/third-parties/google';
import Footer from '@organisms/Footer/Footer';
import Header from '@organisms/Header/Header';
import { HeroVariant, PaletteVariants } from '@type-declarations/hero';
import { Image } from '@type-declarations/media';
import { Notification as NotificationType } from '@type-declarations/notification';
import { Page } from '@type-declarations/page';
import { SeoProps } from '@type-declarations/seo';
import { getColorPalette } from '@utils/getColorPalette';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { PropsWithChildren, useMemo } from 'react';

const Notification = dynamic(
  () => import('@organisms/Notification/Notification'),
  {
    ssr: false,
  },
);

interface Props extends PropsWithChildren {
  title?: string;
  suffix?: string;
  heroVariant: HeroVariant | null;
  seo?: SeoProps;
  image?: Image;
  introText?: string;
  page?: Page;
  notification: NotificationType | null;
}

function BaseTemplate({
  children,
  title,
  suffix = 'Explore Breda',
  seo,
  heroVariant = 'small',
  image,
  introText,
  page,
  notification,
}: Props) {
  const colorPalette: PaletteVariants = useMemo(
    () => getColorPalette({ page }),
    [page],
  );
  const isCampaignPage = page?.typename === 'ZigzagCampaignPage';

  // If the URL has a query parameter "app=true", we don't want to render the base template
  // This is used to render the page in the app (requested by Unlock agency)
  const { query } = useRouter();
  if (query.app === 'true') {
    return <main>{children}</main>;
  }

  return (
    <>
      <GoogleTagManager gtmId={process.env.GTM_CODE as string} />

      <ColorPalette colorPalette={colorPalette} />

      <Seo
        title={title}
        suffix={suffix}
        seo={seo}
        image={image}
        description={introText}
      />

      <Header heroVariant={heroVariant} isCampaignPage={isCampaignPage} />
      <AccessibilityPanel />

      {notification && <Notification notification={notification} />}

      <main>{children}</main>
      <Footer isCampaignPage={isCampaignPage} />
    </>
  );
}

export default BaseTemplate;
